/**
 * Disclosures API service.
 *
 * Thin client over the standalone ``/api/v1/disclosures`` backend.
 * Mirrors the backend Pydantic shapes loosely — only the fields the
 * demo UI actually consumes are typed here.
 */

import { apiRequest } from "../client";

// ── Types ─────────────────────────────────────────────────────────────

export type DisclosureCaseStage =
  | "intake"
  | "analyse"
  | "draft"
  | "signoff"
  // Legacy stages kept for in-flight cases.
  | "threshold"
  | "triage"
  | "completeness"
  | "assessment"
  | "review"
  | "filing"
  | "submission"
  | "complete";

export type DisclosureGrade =
  | "ready-to-file"
  | "minor-issues"
  | "needs-investigation"
  | "blocking-gap"
  | "do-not-file"
  | null;

export type DisclosureRole = "analyst" | "supervisor" | "compliance";

export type RuleStatus = "pending" | "passed" | "failed" | "unclear" | "n/a";

export type CompletenessStatus = "pending" | "complete" | "incomplete";

export type ThresholdDirection = "cross-up" | "cross-down";

export interface ThresholdCrossing {
  holder: string;
  issuer: string;
  instrument_class: string;
  direction: ThresholdDirection;
  holding_pct: number;
  threshold_tier: string;
  trade_date: string;
  earliest_filing_deadline?: string | null;
  suggested_form?: string | null;
  notes?: string | null;
}

export interface AssessmentRuleResult {
  rule_id: string;
  label: string;
  role?: DisclosureRole | null;
  status: RuleStatus;
  evidence?: string | null;
  document_refs?: string[];
}

export interface GroupAssessment {
  group_id: string;
  group_name: string;
  status: RuleStatus;
  reasoning?: string | null;
  rules: AssessmentRuleResult[];
}

export interface Assessment {
  groups: GroupAssessment[];
}

export interface CompletenessCheck {
  status: CompletenessStatus;
  summary?: string | null;
  missing_documents: Array<{
    document_type: string;
    document_name: string;
    required: boolean;
    reason: string;
    role?: DisclosureRole;
  }>;
  present_count: number;
  required_count: number;
  can_proceed_to_assessment: boolean;
}

export interface RoleGrade {
  role: DisclosureRole;
  grade: DisclosureGrade;
  confidence: number;
  primary_flag?: string | null;
}

export interface DisclosureReview {
  role_grades: RoleGrade[];
  summary: string;
}

export interface RemedialAction {
  id: string;
  title: string;
  description: string;
  role?: DisclosureRole | null;
  priority: "high" | "medium" | "low";
  source: string;
  rule_id?: string | null;
  group_id?: string | null;
}

export interface DisclosureCase {
  id: string;
  company_id: string;
  title?: string | null;
  holder?: string | null;
  issuer?: string | null;
  desk?: string | null;
  stage: DisclosureCaseStage;
  grade?: DisclosureGrade;
  case_type: string;
  case_type_confidence?: number | null;
  case_type_reasoning?: string | null;
  threshold_crossings: ThresholdCrossing[];
  filing_deadline?: string | null;
  document_count: number;
  assessment: Assessment;
  completeness?: CompletenessCheck | null;
  review?: DisclosureReview | null;
  remedial_actions: RemedialAction[];
  submission_reference?: string | null;
  submitted_at?: string | null;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
}

export interface DisclosureDocument {
  id: string;
  case_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  classification?: string | null;
  classification_confidence?: number | null;
  classification_reasoning?: string | null;
  summary?: string | null;
  document_evidence?: Record<string, any> | null;
  valid: boolean;
  validity_reasoning?: string | null;
  extracted_text?: string | null;
  created_at: string;
}

export interface ArtifactRenderResponse {
  artifact_id: string;
  case_id: string;
  kind: "filing_draft" | "audit_pack" | string;
  version: number;
  title: string;
}

// ── Endpoints ─────────────────────────────────────────────────────────

const BASE = "/disclosures";

export async function listCases(): Promise<DisclosureCase[]> {
  return apiRequest({ url: `${BASE}/cases`, method: "GET" });
}

export async function getCase(caseId: string): Promise<DisclosureCase> {
  return apiRequest({ url: `${BASE}/cases/${caseId}`, method: "GET" });
}

export async function createCase(body: {
  title?: string;
  holder?: string;
  issuer?: string;
  desk?: string;
  metadata?: Record<string, any>;
}): Promise<DisclosureCase> {
  return apiRequest({ url: `${BASE}/cases`, method: "POST", data: body });
}

export async function deleteCase(caseId: string): Promise<void> {
  await apiRequest({ url: `${BASE}/cases/${caseId}`, method: "DELETE" });
}

/** Launch Clara on this disclosure case (POST /disclosures/cases/{id}/agentic-mission). */
export async function launchDisclosureMission(caseId: string): Promise<{
  mission_id: string;
  task_id: string | null;
  case_id?: string | null;
}> {
  return apiRequest({
    url: `${BASE}/cases/${caseId}/agentic-mission`,
    method: "POST",
  });
}

export async function listDocuments(
  caseId: string,
): Promise<DisclosureDocument[]> {
  return apiRequest({
    url: `${BASE}/cases/${caseId}/documents`,
    method: "GET",
  });
}

export async function uploadDocument(
  caseId: string,
  file: File,
): Promise<DisclosureDocument> {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest({
    url: `${BASE}/cases/${caseId}/documents`,
    method: "POST",
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function detectThreshold(
  caseId: string,
  body: { csv_text: string; issuer_jurisdiction?: string },
): Promise<{
  case_id: string;
  crossings: ThresholdCrossing[];
  filing_deadline?: string | null;
  suggested_form?: string | null;
  stage: string;
}> {
  return apiRequest({
    url: `${BASE}/agentic/cases/${caseId}/detect-threshold`,
    method: "POST",
    data: body,
  });
}

export async function detectJurisdiction(
  caseId: string,
  body: { issuer_jurisdiction?: string; intent_active?: boolean },
): Promise<{ case_type: string; confidence: number; reasoning: string }> {
  return apiRequest({
    url: `${BASE}/agentic/cases/${caseId}/detect-jurisdiction`,
    method: "POST",
    data: body,
  });
}

export async function applyCaseType(
  caseId: string,
  body: {
    case_type: string;
    confidence?: number;
    reasoning?: string;
    filing_deadline?: string;
  },
): Promise<DisclosureCase> {
  return apiRequest({
    url: `${BASE}/agentic/cases/${caseId}/case-type`,
    method: "POST",
    data: body,
  });
}

export async function runCompleteness(
  caseId: string,
): Promise<DisclosureCase> {
  return apiRequest({
    url: `${BASE}/agentic/cases/${caseId}/check-completeness`,
    method: "POST",
    data: {},
  });
}

export async function renderFiling(caseId: string): Promise<ArtifactRenderResponse> {
  return apiRequest({
    url: `${BASE}/agentic/cases/${caseId}/render-filing`,
    method: "POST",
    data: {},
  });
}

export async function renderAuditPack(caseId: string): Promise<ArtifactRenderResponse> {
  return apiRequest({
    url: `${BASE}/agentic/cases/${caseId}/render-audit`,
    method: "POST",
    data: {},
  });
}

/** @deprecated use {@link renderAuditPack}. Kept for the audit embed. */
export const renderAudit = renderAuditPack;

export async function submitFiling(
  caseId: string,
  reference?: string,
): Promise<DisclosureCase> {
  return apiRequest({
    url: `${BASE}/agentic/cases/${caseId}/submit`,
    method: "POST",
    data: { reference: reference || null },
  });
}

export interface AuditEvent {
  at: string;
  event_type: string;
  summary: string;
  details?: Record<string, unknown>;
  actor?: string;
}

export interface AuditLogResponse {
  case_id: string;
  events: AuditEvent[];
  markdown_preview: string;
  artifact_generated_at?: string | null;
}

export async function getAuditLog(caseId: string): Promise<AuditLogResponse> {
  return apiRequest({
    url: `${BASE}/cases/${caseId}/audit-log`,
    method: "GET",
  });
}

// ── Peer review ───────────────────────────────────────────────────────

export type PeerAgreement = "agree" | "partial" | "disagree";
export type PeerWhoIsRight = "agent" | "human" | "both" | "unclear";

export interface PeerReviewDivergence {
  topic: string;
  agent_view: string;
  human_view: string;
  who_is_right: PeerWhoIsRight;
  explanation: string;
  rule_reference?: string | null;
}

export interface PeerReviewComparison {
  case_title: string;
  case_type_label: string;
  agent_grade?: DisclosureGrade;
  human_grade?: DisclosureGrade;
  agreement: PeerAgreement;
  summary: string;
  divergences: PeerReviewDivergence[];
  learnings: string[];
}

export interface PeerReviewInputManifest {
  files: Array<{ filename: string; size_bytes: number; mime_type: string }>;
  human_review_text_char_count: number;
}

export interface PeerReviewRun {
  id: string;
  case_id: string;
  run_by?: string | null;
  comparison: PeerReviewComparison;
  input_manifest: PeerReviewInputManifest;
  notes: string;
  created_at: string;
}

export async function runPeerReview(
  caseId: string,
  options: { humanReviewText?: string; files?: File[] },
): Promise<PeerReviewRun> {
  const formData = new FormData();
  if (options.humanReviewText) {
    formData.append("human_review_text", options.humanReviewText);
  }
  for (const file of options.files ?? []) {
    formData.append("files", file);
  }
  return apiRequest({
    url: `${BASE}/cases/${caseId}/peer-review`,
    method: "POST",
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function listPeerReviewRuns(
  caseId: string,
  limit = 20,
): Promise<PeerReviewRun[]> {
  return apiRequest({
    url: `${BASE}/cases/${caseId}/peer-review`,
    method: "GET",
    params: { limit },
  });
}

export async function updatePeerReviewNotes(
  caseId: string,
  runId: string,
  notes: string,
): Promise<PeerReviewRun> {
  return apiRequest({
    url: `${BASE}/cases/${caseId}/peer-review/${runId}/notes`,
    method: "PATCH",
    data: { notes },
  });
}

